import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiClient } from "../../services/api";
import { LEAD_DISPATCH_ERROR_MESSAGES } from "../../services/shared";
import { DispatcherRule, Rule, RuleType, SimpleRule } from "../../types";

type ActiveFilter = "all" | "true" | "false";
type TypeFilter = "all" | RuleType;

export default function RulesList() {
  const navigate = useNavigate();

  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterName, setFilterName] = useState("");
  const [filterType, setFilterType] = useState<TypeFilter>("all");
  const [filterActive, setFilterActive] = useState<ActiveFilter>("all");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRule, setMenuRule] = useState<Rule | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Rule | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [referencedBy, setReferencedBy] = useState<string[] | null>(null);

  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (filterType !== "all") params.type = filterType;
      if (filterActive !== "all") params.active = filterActive;
      const res = await apiClient.get("/v1/lead-dispatch/rules", { params });
      const data: Rule[] = res.data?.data?.rules ?? [];
      setRules(data);
    } catch (e) {
      console.error(e);
      setError("Impossibile caricare le regole");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterActive]);

  const filtered = useMemo(() => {
    const q = filterName.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((r) => r.displayName.toLowerCase().includes(q));
  }, [rules, filterName]);

  const openMenu = (e: React.MouseEvent<HTMLElement>, rule: Rule) => {
    setAnchorEl(e.currentTarget);
    setMenuRule(rule);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuRule(null);
  };

  const handleToggleActive = async (rule: Rule) => {
    setTogglingId(rule.id);
    try {
      await apiClient.patch(`/v1/lead-dispatch/rules/${rule.id}`, {
        active: !rule.active,
      });
      setRules((prev) =>
        prev.map((r) => (r.id === rule.id ? ({ ...r, active: !r.active } as Rule) : r))
      );
    } catch (e) {
      console.error(e);
      setError("Errore nell'aggiornamento dello stato");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    setReferencedBy(null);
    try {
      await apiClient.delete(`/v1/lead-dispatch/rules/${deleteTarget.id}`);
      setRules((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      if (axios.isAxiosError(e) && e.response?.status === 422) {
        const errCode = e.response.data?.error;
        if (errCode === "REFERENCED_BY_DISPATCHER") {
          setReferencedBy(e.response.data?.data?.referenced_by ?? []);
          setDeleteError(LEAD_DISPATCH_ERROR_MESSAGES.REFERENCED_BY_DISPATCHER);
        } else {
          setDeleteError(
            LEAD_DISPATCH_ERROR_MESSAGES[errCode] ??
              "Impossibile eliminare la regola"
          );
        }
      } else {
        setDeleteError("Errore durante l'eliminazione della regola");
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Lead Dispatching — Regole
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/lead-dispatch/rules/new")}
        >
          Nuova regola
        </Button>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Filtra per nome..."
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          sx={{ width: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="filter-type">Tipo</InputLabel>
          <Select
            labelId="filter-type"
            label="Tipo"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as TypeFilter)}
          >
            <MenuItem value="all">Tutti</MenuItem>
            <MenuItem value="simple">Simple</MenuItem>
            <MenuItem value="dispatcher">Dispatcher</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="filter-active">Stato</InputLabel>
          <Select
            labelId="filter-active"
            label="Stato"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as ActiveFilter)}
          >
            <MenuItem value="all">Tutti</MenuItem>
            <MenuItem value="true">Attive</MenuItem>
            <MenuItem value="false">Disattive</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell width={120}>Tipo</TableCell>
                <TableCell>Destinazione</TableCell>
                <TableCell width={140}>Aggiornata</TableCell>
                <TableCell width={80} align="center">
                  Attiva
                </TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 3 }}
                    >
                      Nessuna regola trovata
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.displayName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontFamily="monospace"
                    >
                      {rule.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rule.type === "simple" ? "Simple" : "Dispatcher"}
                      size="small"
                      color={rule.type === "simple" ? "primary" : "secondary"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {rule.type === "simple" ? (
                      <SimpleDestinationCell rule={rule} />
                    ) : (
                      <DispatcherDestinationCell rule={rule} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {new Date(rule.updated_at).toLocaleString("it-IT")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={rule.active}
                      size="small"
                      disabled={togglingId === rule.id}
                      onChange={() => handleToggleActive(rule)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => openMenu(e, rule)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {menuRule?.type === "simple" && (
          <MenuItem
            onClick={() => {
              if (menuRule) {
                navigate("/lead-dispatch/rules/new", {
                  state: { duplicateFrom: menuRule },
                });
              }
              closeMenu();
            }}
          >
            Duplica
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menuRule) navigate(`/lead-dispatch/rules/${menuRule.id}`);
            closeMenu();
          }}
        >
          Modifica
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteTarget(menuRule);
            setDeleteError(null);
            setReferencedBy(null);
            closeMenu();
          }}
          sx={{ color: "error.main" }}
        >
          Elimina
        </MenuItem>
      </Menu>

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Elimina regola</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare la regola{" "}
            <strong>{deleteTarget?.displayName}</strong>? Questa operazione non
            può essere annullata.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
              {referencedBy && referencedBy.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                  {referencedBy.map((id) => (
                    <li key={id}>
                      <Typography variant="body2" fontFamily="monospace">
                        {id}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Chiudi
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            disabled={deleting || !!referencedBy}
          >
            {deleting ? "Eliminazione..." : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SimpleDestinationCell({ rule }: { rule: SimpleRule }) {
  return (
    <Stack spacing={0.5}>
      <Typography
        variant="caption"
        fontFamily="monospace"
        sx={{ wordBreak: "break-all" }}
      >
        {rule.destination.url}
      </Typography>
      <Chip
        label={rule.destination.method}
        size="small"
        variant="outlined"
        sx={{ fontFamily: "monospace", fontSize: "0.65rem", alignSelf: "flex-start" }}
      />
    </Stack>
  );
}

function DispatcherDestinationCell({ rule }: { rule: DispatcherRule }) {
  const scheduleCount = rule.schedules?.length ?? 0;
  const dayCount = new Set(rule.schedules?.map((s) => s.dayOfWeek) ?? []).size;
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption">
        {scheduleCount} schedule su {dayCount} {dayCount === 1 ? "giorno" : "giorni"}
      </Typography>
      {rule.fallback?.simpleRuleId && (
        <Typography
          variant="caption"
          color="text.secondary"
          fontFamily="monospace"
        >
          fallback: {rule.fallback.simpleRuleId}
        </Typography>
      )}
    </Stack>
  );
}
